import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarMonth,
  TrendingUp,
  AccessTime,
  Download,
  Refresh,
  BarChart,
  Assessment,
  Person,
  DateRange,
  PictureAsPdf,
  TableChart,
} from '@mui/icons-material';

const ReportFilters = ({
  isAdmin,
  reportType,
  handleReportTypeChange,
  selectedEmployee,
  setSelectedEmployee,
  employees,
  selectedYear,
  setSelectedYear,
  selectedMonth,
  setSelectedMonth,
  selectedWeekStart,
  setSelectedWeekStart,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  loading,
  exportFormat,
  setExportFormat,
  handleExport,
  exporting,
  showExportOptions,
  setShowExportOptions,
  showStats,
  setShowStats,
  showBarChart,
  setShowBarChart,
}) => {
  // Years array
  const years = [2023, 2024, 2025, 2026, 2027];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate week start dates for the current year
  const getWeekStartOptions = () => {
    const options = [];
    const today = new Date();
    const startDate = new Date(selectedYear, 0, 1);
    const endDate = new Date(selectedYear, 11, 31);
    
    let currentDate = new Date(startDate);
    // Find first Monday of the year
    while (currentDate.getDay() !== 1 && currentDate <= endDate) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    while (currentDate <= endDate) {
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekStartStr = currentDate.toISOString().split('T')[0];
      const weekEndStr = weekEnd.toISOString().split('T')[0];
      const isCurrentWeek = currentDate <= today && weekEnd >= today;
      
      options.push({
        value: weekStartStr,
        label: `${weekStartStr} to ${weekEndStr}`,
        isCurrent: isCurrentWeek,
      });
      
      currentDate.setDate(currentDate.getDate() + 7);
    }
    return options;
  };

  const weekOptions = getWeekStartOptions();

  const handleReportTypeClick = (type) => {
    console.log('Report filter button clicked:', type);
    handleReportTypeChange(type);
  };

  return (
    <Card sx={{ mb: 3, bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
      <CardContent>
        {/* Report Type Buttons */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#B4C4A8', mb: 1 }}>
            Report Type
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {isAdmin && (
              <Button
                variant={reportType === 'daily' ? 'contained' : 'outlined'}
                onClick={() => handleReportTypeClick('daily')}
                startIcon={<CalendarMonth />}
                sx={{
                  bgcolor: reportType === 'daily' ? '#D4AF37' : 'transparent',
                  color: reportType === 'daily' ? '#1A2E14' : '#D4AF37',
                  borderColor: '#D4AF37',
                  '&:hover': {
                    bgcolor: reportType === 'daily' ? '#C19B2E' : 'rgba(212,175,55,0.1)',
                    borderColor: '#D4AF37',
                  }
                }}
              >
                Daily Report
              </Button>
            )}
            
            {isAdmin && (
              <Button
                variant={reportType === 'weekly' ? 'contained' : 'outlined'}
                onClick={() => handleReportTypeClick('weekly')}
                startIcon={<TrendingUp />}
                sx={{
                  bgcolor: reportType === 'weekly' ? '#D4AF37' : 'transparent',
                  color: reportType === 'weekly' ? '#1A2E14' : '#D4AF37',
                  borderColor: '#D4AF37',
                  '&:hover': {
                    bgcolor: reportType === 'weekly' ? '#C19B2E' : 'rgba(212,175,55,0.1)',
                    borderColor: '#D4AF37',
                  }
                }}
              >
                Weekly Report
              </Button>
            )}
            
            <Button
              variant={reportType === 'monthly' ? 'contained' : 'outlined'}
              onClick={() => handleReportTypeClick('monthly')}
              startIcon={<AccessTime />}
              sx={{
                bgcolor: reportType === 'monthly' ? '#D4AF37' : 'transparent',
                color: reportType === 'monthly' ? '#1A2E14' : '#D4AF37',
                borderColor: '#D4AF37',
                '&:hover': {
                  bgcolor: reportType === 'monthly' ? '#C19B2E' : 'rgba(212,175,55,0.1)',
                  borderColor: '#D4AF37',
                }
              }}
            >
              Monthly Report
            </Button>
            
            {isAdmin && (
              <Button
                variant={reportType === 'employee_wise' ? 'contained' : 'outlined'}
                onClick={() => handleReportTypeClick('employee_wise')}
                startIcon={<Person />}
                sx={{
                  bgcolor: reportType === 'employee_wise' ? '#D4AF37' : 'transparent',
                  color: reportType === 'employee_wise' ? '#1A2E14' : '#D4AF37',
                  borderColor: '#D4AF37',
                  '&:hover': {
                    bgcolor: reportType === 'employee_wise' ? '#C19B2E' : 'rgba(212,175,55,0.1)',
                    borderColor: '#D4AF37',
                  }
                }}
              >
                Employee-wise
              </Button>
            )}
          </Stack>
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Employee Filter */}
          {isAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B4C4A8' }}>Employee</InputLabel>
                <Select
                  value={selectedEmployee}
                  label="Employee"
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees && employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Year Filter - Show for weekly and monthly reports */}
          {(reportType === 'weekly' || reportType === 'monthly' || reportType === 'employee_wise') && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B4C4A8' }}>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Month Filter - Show for monthly and employee-wise reports */}
          {(reportType === 'monthly' || reportType === 'employee_wise') && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B4C4A8' }}>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  {months.map((month, index) => (
                    <MenuItem key={index} value={index}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Week Selector - Show for weekly report */}
          {reportType === 'weekly' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B4C4A8' }}>Week Starting</InputLabel>
                <Select
                  value={selectedWeekStart}
                  label="Week Starting"
                  onChange={(e) => setSelectedWeekStart(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  {weekOptions.map((week) => (
                    <MenuItem key={week.value} value={week.value}>
                      {week.label} {week.isCurrent && <Chip label="Current" size="small" sx={{ ml: 1, bgcolor: '#D4AF37', color: '#1A2E14' }} />}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Export Button */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <Download />}
              fullWidth
              onClick={handleExport}
              disabled={exporting || loading}
              sx={{
                bgcolor: '#D4AF37',
                color: '#1A2E14',
                '&:hover': { bgcolor: '#C19B2E' }
              }}
            >
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </Grid>

          {/* Refresh Button */}
          <Grid item xs={12} sm={6} md={1}>
            <Tooltip title="Refresh Report">
              <IconButton
                onClick={() => window.location.reload()}
                disabled={loading}
                sx={{ color: '#D4AF37' }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Toggle Options */}
        <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Tooltip title="Show/Hide Statistics Cards">
            <Chip
              icon={<Assessment />}
              label={showStats ? "Hide Stats" : "Show Stats"}
              onClick={() => setShowStats(!showStats)}
              sx={{
                bgcolor: showStats ? '#D4AF37' : 'transparent',
                color: showStats ? '#1A2E14' : '#D4AF37',
                border: '1px solid #D4AF37',
                cursor: 'pointer'
              }}
            />
          </Tooltip>
          
          <Tooltip title="Show/Hide Bar Chart">
            <Chip
              icon={<BarChart />}
              label={showBarChart ? "Hide Chart" : "Show Chart"}
              onClick={() => setShowBarChart(!showBarChart)}
              sx={{
                bgcolor: showBarChart ? '#D4AF37' : 'transparent',
                color: showBarChart ? '#1A2E14' : '#D4AF37',
                border: '1px solid #D4AF37',
                cursor: 'pointer'
              }}
            />
          </Tooltip>

          <Tooltip title="Export Format">
            <Chip
              icon={exportFormat === 'csv' ? <TableChart /> : <PictureAsPdf />}
              label={exportFormat.toUpperCase()}
              onClick={() => setExportFormat(exportFormat === 'csv' ? 'pdf' : 'csv')}
              sx={{
                bgcolor: 'transparent',
                color: '#D4AF37',
                border: '1px solid #D4AF37',
                cursor: 'pointer'
              }}
            />
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ReportFilters;